/**
 * AiAnalyzeSection — the "Analyse IA" collapsible section of the ATS sidebar.
 *
 * Shows three action buttons (analyze / compare / optimize) and, when the
 * user has run them, the three result cards with structured scores, lists
 * of strengths / gaps / suggestions, and keyword chips.
 *
 * Pure UI component — all state and handlers come from the parent.
 */

import { memo } from 'react';
import {
  ChartNoAxesCombined,
  Microscope,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Loader2,
  Sparkles,
  Target,
} from 'lucide-react';
import type { getTType } from '../../contexts/I18nContext';
import type {
  AiAnalyzeStructured,
  AiCompareStructured,
  AiAtsOptimizeStructured,
} from '../../lib/ai-types';
import type {
  AvailableModels,
  PipelineProgress,
} from '../../hooks/useAutoIa';
import { BadgeCache } from '../ui/BadgeCache';

export interface AiAnalyzeSectionProps {
  t: getTType;

  // Section expand/collapse
  isExpanded: boolean;
  onToggleExpanded: () => void;

  // Available models (display only)
  availableModels: AvailableModels;

  // Action handlers
  onAnalyzeClick: () => Promise<void> | void;
  onCompareClick: () => Promise<void> | void;
  onOptimizeClick: () => Promise<void> | void;

  // Loading flags
  isAiAnalyzing: boolean;
  isAiComparing: boolean;
  isAiOptimizing: boolean;

  // Cache indicators
  showAnalyzeCached: boolean;
  showCompareCached: boolean;
  showOptimizeCached: boolean;

  // Raw text fallback (when structured parse failed)
  aiAnalysisResult: string | null;
  aiCompareResult: string | null;
  aiOptimizeResult: string | null;

  // Structured results
  aiAnalysisStructured: AiAnalyzeStructured | null;
  aiCompareStructured: AiCompareStructured | null;
  aiOptimizeStructured: AiAtsOptimizeStructured | null;

  // Gate: disable buttons while autoIa / extension pipeline run
  isAutoIaRunning: boolean;
  pipelineProgress: PipelineProgress | null;
  onAutoIa: () => void | Promise<void>;

  // Gate: compare / optimize require a job description
  effectiveJobDescription: string;
}

export const AiAnalyzeSection = memo(function AiAnalyzeSection(props: AiAnalyzeSectionProps) {
  const {
    t,
    isExpanded,
    onToggleExpanded,
    availableModels,
    onAnalyzeClick,
    onCompareClick,
    onOptimizeClick,
    isAiAnalyzing,
    isAiComparing,
    isAiOptimizing,
    showAnalyzeCached,
    showCompareCached,
    showOptimizeCached,
    aiAnalysisResult,
    aiCompareResult,
    aiOptimizeResult,
    aiAnalysisStructured,
    aiCompareStructured,
    aiOptimizeStructured,
    isAutoIaRunning,
    pipelineProgress,
    onAutoIa,
    effectiveJobDescription,
  } = props;

  const hasJob = !!effectiveJobDescription?.trim();
  const pipelineBlocking = isAutoIaRunning || pipelineProgress?.kind === 'extensionAnalyze';
  const hasAnyResult =
    aiAnalysisResult != null ||
    aiCompareResult != null ||
    aiOptimizeResult != null ||
    !!aiAnalysisStructured ||
    !!aiCompareStructured ||
    !!aiOptimizeStructured;

  return (
    <div className="space-y-4">
      <h2
        className="flex items-center justify-between gap-2 text-base font-semibold text-white cursor-pointer hover:text-blue-300 transition-colors border-t border-white/10 pt-4"
        onClick={onToggleExpanded}
      >
        <span className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-400">
            <Sparkles size={18} />
          </div>
          Analyse IA
        </span>
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </h2>

      {isExpanded && (
        <div className="bg-gradient-to-br from-violet-500/5 to-purple-500/5 rounded-xl border border-violet-500/20 p-4 space-y-4">
          <p className="text-[9px] text-white/50">
            Analyse qualitative et comparaison à l'offre via IA.
            {availableModels.model1 && (
              <>
                {' '}
                Modèle 1: <code className="text-violet-400">{availableModels.model1}</code>
              </>
            )}
            {availableModels.model2 && (
              <>
                {' '}
                • Modèle 2: <code className="text-violet-400">{availableModels.model2}</code>
              </>
            )}
            {!availableModels.model1 && !availableModels.model2 && (
              <>
                {' '}
                Configurez OPENROUTER_API_KEY (Modèle 1) et/ou MINIMAX_API_KEY (Modèle 2) dans .env
                ou sur le serveur VPS.
              </>
            )}
          </p>

          {/* Primary CTA: Adapter mon CV (Auto IA pipeline) */}
          <button
            type="button"
            onClick={() => void onAutoIa()}
            disabled={!hasJob || pipelineBlocking}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold transition-all shadow-lg shadow-blue-600/20"
          >
            {isAutoIaRunning ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Microscope size={15} />
            )}
            {isAutoIaRunning ? 'Adaptation en cours…' : 'Adapter mon CV'}
          </button>

          {/* Progress when Auto IA runs */}
          {isAutoIaRunning && pipelineProgress && !pipelineProgress.successMessage && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
              {pipelineProgress.fallbackMessage ? (
                <div className="flex items-center gap-2 text-amber-300 text-xs">
                  <Loader2 size={12} className="animate-spin shrink-0" />
                  {pipelineProgress.fallbackMessage}
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {pipelineProgress.labels.map((label, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs">
                      {i < pipelineProgress.step ? (
                        <span className="size-3.5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                          <span className="size-1.5 rounded-full bg-white" />
                        </span>
                      ) : i === pipelineProgress.step ? (
                        <Loader2 size={12} className="text-blue-400 shrink-0 animate-spin" />
                      ) : (
                        <span className="size-3.5 rounded-full border border-white/20 shrink-0" />
                      )}
                      <span className={i <= pipelineProgress.step ? 'text-white/80' : 'text-white/30'}>{label}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Granular buttons for power users */}
          <p className="text-[9px] text-white/30 text-center">— ou lancer chaque étape séparément —</p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => void onAnalyzeClick()}
              disabled={isAiAnalyzing || pipelineBlocking}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-violet-500/30 bg-blue-500/10 hover:bg-blue-500/20 text-violet-300 text-sm font-bold transition-all disabled:opacity-50"
            >
              {isAiAnalyzing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : showAnalyzeCached || aiAnalysisStructured ? (
                <CheckCircle2 size={14} className="text-green-500" />
              ) : (
                <Sparkles size={14} />
              )}
              {isAiAnalyzing ? t('ats.analyzing') : 'Analyse CV (IA)'}
            </button>
            <button
              type="button"
              onClick={() => void onCompareClick()}
              disabled={isAiComparing || !hasJob || pipelineBlocking}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-violet-500/30 bg-blue-500/10 hover:bg-blue-500/20 text-violet-300 text-sm font-bold transition-all disabled:opacity-50"
            >
              {isAiComparing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : showCompareCached || aiCompareStructured ? (
                <CheckCircle2 size={14} className="text-green-500" />
              ) : (
                <Target size={14} />
              )}
              {isAiComparing ? 'Comparaison...' : 'Comparer CV ↔ Offre (IA)'}
            </button>
            <button
              type="button"
              onClick={() => void onOptimizeClick()}
              disabled={isAiOptimizing || !hasJob || pipelineBlocking}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-violet-500/30 bg-blue-500/10 hover:bg-blue-500/20 text-violet-300 text-sm font-bold transition-all disabled:opacity-50"
            >
              {isAiOptimizing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : showOptimizeCached || aiOptimizeStructured ? (
                <CheckCircle2 size={14} className="text-green-500" />
              ) : (
                <ChartNoAxesCombined size={14} />
              )}
              {isAiOptimizing ? t('ats.optimizing') : `${t('sections.optimisationAts')} (IA)`}
            </button>
          </div>

          {/* Results */}
          {hasAnyResult && (
            <div className="space-y-4 max-h-[55vh] overflow-y-auto scrollbar-hide pr-1">
              {/* --- Analyze card --- */}
              {aiAnalysisResult != null && (
                <div className="rounded-xl border border-violet-500/25 bg-black/30 overflow-hidden">
                  <div className="px-3 py-2 border-b border-violet-500/20 bg-blue-500/10">
                    <p className="text-[9px] text-violet-300 uppercase font-bold flex items-center gap-1.5">
                      <ChartNoAxesCombined size={12} /> Analyse CV{' '}
                      <BadgeCache
                        cached={showAnalyzeCached}
                        label={showAnalyzeCached ? 'Cache' : null}
                      />
                    </p>
                  </div>
                  <div className="p-3 space-y-3">
                    {aiAnalysisStructured ? (
                      <>
                        {aiAnalysisStructured.summary && (
                          <p className="text-sm text-white/90 leading-relaxed">
                            {aiAnalysisStructured.summary}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {typeof aiAnalysisStructured.atsScore === 'number' && (
                            <span className="px-2 py-0.5 rounded-md bg-blue-500/25 text-violet-200 text-[9px] font-semibold">
                              ATS {aiAnalysisStructured.atsScore}%
                            </span>
                          )}
                          {typeof aiAnalysisStructured.structureScore === 'number' && (
                            <span className="px-2 py-0.5 rounded-md bg-white/10 text-white/80 text-[9px]">
                              Structure {aiAnalysisStructured.structureScore}%
                            </span>
                          )}
                          {typeof aiAnalysisStructured.clarityScore === 'number' && (
                            <span className="px-2 py-0.5 rounded-md bg-white/10 text-white/80 text-[9px]">
                              Clarté {aiAnalysisStructured.clarityScore}%
                            </span>
                          )}
                          {typeof aiAnalysisStructured.keywordsScore === 'number' && (
                            <span className="px-2 py-0.5 rounded-md bg-white/10 text-white/80 text-[9px]">
                              Mots-clés {aiAnalysisStructured.keywordsScore}%
                            </span>
                          )}
                        </div>
                        {aiAnalysisStructured.strengths?.length > 0 && (
                          <div>
                            <p className="text-sm text-green-500/90 uppercase font-bold mb-1">
                              Points forts
                            </p>
                            <ul className="list-disc list-inside text-[9px] text-white/85 space-y-0.5">
                              {aiAnalysisStructured.strengths.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {aiAnalysisStructured.improvements?.length > 0 && (
                          <div>
                            <p className="text-sm text-amber-400/90 uppercase font-bold mb-1">
                              À améliorer
                            </p>
                            <ul className="list-disc list-inside text-[9px] text-white/85 space-y-0.5">
                              {aiAnalysisStructured.improvements.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {aiAnalysisStructured.suggestions?.length > 0 && (
                          <div>
                            <p className="text-sm text-violet-400/90 uppercase font-bold mb-1">
                              Suggestions
                            </p>
                            <ul className="space-y-1.5">
                              {aiAnalysisStructured.suggestions.map((s, i) => (
                                <li key={i} className="text-[9px]">
                                  {s.title && (
                                    <span className="font-semibold text-white/90">
                                      {s.title} —{' '}
                                    </span>
                                  )}
                                  <span className="text-white/80">{s.text}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-[9px] text-white/80 whitespace-pre-wrap leading-relaxed">
                        {aiAnalysisResult}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* --- Compare card --- */}
              {aiCompareResult != null && (
                <div className="rounded-xl border border-violet-500/25 bg-black/30 overflow-hidden">
                  <div className="px-3 py-2 border-b border-violet-500/20 bg-blue-500/10">
                    <p className="text-[9px] text-violet-300 uppercase font-bold flex items-center gap-1.5">
                      <Target size={12} /> Comparaison à l'offre{' '}
                      <BadgeCache
                        cached={showCompareCached}
                        label={showCompareCached ? 'Cache' : null}
                      />
                    </p>
                  </div>
                  <div className="p-3 space-y-3">
                    {aiCompareStructured ? (
                      <>
                        {aiCompareStructured.summary && (
                          <p className="text-sm text-white/90 leading-relaxed">
                            {aiCompareStructured.summary}
                          </p>
                        )}
                        {typeof aiCompareStructured.matchScore === 'number' && (
                          <div className="flex items-center gap-2">
                            <span className="px-2.5 py-1 rounded-lg bg-blue-500/30 text-violet-200 text-sm font-bold">
                              Adéquation {aiCompareStructured.matchScore}%
                            </span>
                          </div>
                        )}
                        {aiCompareStructured.strengths?.length > 0 && (
                          <div>
                            <p className="text-sm text-green-500/90 uppercase font-bold mb-1">
                              Points qui matchent
                            </p>
                            <ul className="list-disc list-inside text-[9px] text-white/85 space-y-0.5">
                              {aiCompareStructured.strengths.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {aiCompareStructured.gaps?.length > 0 && (
                          <div>
                            <p className="text-sm text-amber-400/90 uppercase font-bold mb-1">
                              Manques / écarts
                            </p>
                            <ul className="list-disc list-inside text-[9px] text-white/85 space-y-0.5">
                              {aiCompareStructured.gaps.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {aiCompareStructured.suggestions?.length > 0 && (
                          <div>
                            <p className="text-sm text-violet-400/90 uppercase font-bold mb-1">
                              Suggestions
                            </p>
                            <ul className="space-y-1.5">
                              {aiCompareStructured.suggestions.map((s, i) => (
                                <li key={i} className="text-[9px]">
                                  {s.title && (
                                    <span className="font-semibold text-white/90">
                                      {s.title} —{' '}
                                    </span>
                                  )}
                                  <span className="text-white/80">{s.text}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-[9px] text-white/80 whitespace-pre-wrap leading-relaxed">
                        {aiCompareResult}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* --- Optimize card --- */}
              {aiOptimizeResult != null && (
                <div className="rounded-xl border border-violet-500/25 bg-black/30 overflow-hidden">
                  <div className="px-3 py-2 border-b border-violet-500/20 bg-blue-500/10">
                    <p className="text-[9px] text-violet-300 uppercase font-bold flex items-center gap-1.5">
                      <Sparkles size={12} /> Rapport ATS (IA){' '}
                      <BadgeCache
                        cached={showOptimizeCached}
                        label={showOptimizeCached ? 'Cache' : null}
                      />
                    </p>
                  </div>
                  <div className="p-3 space-y-3">
                    {aiOptimizeStructured ? (
                      <>
                        {aiOptimizeStructured.summary && (
                          <p className="text-sm text-white/90 leading-relaxed">
                            {aiOptimizeStructured.summary}
                          </p>
                        )}
                        {typeof aiOptimizeStructured.atsScore === 'number' && (
                          <span className="inline-block px-2.5 py-1 rounded-lg bg-emerald-500/25 text-emerald-200 text-sm font-bold">
                            Score ATS {aiOptimizeStructured.atsScore}%
                          </span>
                        )}
                        {aiOptimizeStructured.compatibilityNotes?.length > 0 && (
                          <div>
                            <p className="text-sm text-white/70 uppercase font-bold mb-1">
                              Compatibilité
                            </p>
                            <ul className="list-disc list-inside text-[9px] text-white/85 space-y-0.5">
                              {aiOptimizeStructured.compatibilityNotes.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {aiOptimizeStructured.keywordsToAdd?.length > 0 && (
                          <div>
                            <p className="text-sm text-violet-400/90 uppercase font-bold mb-1">
                              Mots-clés à ajouter
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {aiOptimizeStructured.keywordsToAdd.map((k, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-0.5 rounded bg-blue-500/20 text-violet-200 text-[9px]"
                                >
                                  {k}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {aiOptimizeStructured.gaps?.length > 0 && (
                          <div>
                            <p className="text-sm text-amber-400/90 uppercase font-bold mb-1">
                              Écarts
                            </p>
                            <ul className="list-disc list-inside text-[9px] text-white/85 space-y-0.5">
                              {aiOptimizeStructured.gaps.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {aiOptimizeStructured.suggestions?.length > 0 && (
                          <div>
                            <p className="text-sm text-violet-400/90 uppercase font-bold mb-1">
                              Actions (priorité)
                            </p>
                            <ul className="space-y-1.5">
                              {aiOptimizeStructured.suggestions.map((s, i) => (
                                <li key={i} className="text-[9px] flex gap-1.5">
                                  <span
                                    className={`shrink-0 px-1 rounded text-sm font-bold ${
                                      s.priority === 'high'
                                        ? 'bg-rose-500/30 text-rose-200'
                                        : s.priority === 'medium'
                                          ? 'bg-amber-500/25 text-amber-200'
                                          : 'bg-white/15 text-white/70'
                                    }`}
                                  >
                                    {s.priority === 'high'
                                      ? 'Haute'
                                      : s.priority === 'medium'
                                        ? 'Moyenne'
                                        : 'Basse'}
                                  </span>
                                  {s.title && (
                                    <span className="font-semibold text-white/90">
                                      {s.title} —{' '}
                                    </span>
                                  )}
                                  <span className="text-white/80">{s.text}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-[9px] text-white/80 whitespace-pre-wrap leading-relaxed">
                        {aiOptimizeResult}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
